import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Users, Search, Plus, Download, Edit2, Trash2, Upload,
  Camera, X, FileText, AlertCircle, Loader2, ChevronDown,
  GraduationCap, UserCheck, UserX, ChevronRight, Printer,
  BookOpen, TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import api from '../../api';
import { uploadToCloudinary } from '../../utils/cloudinaryUpload';

const GENDERS = ['Male', 'Female'];
const SECTIONS = ['A', 'B', 'C', 'D'];

export default function StudentsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [students, setStudents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState(() => searchParams.get('class_id') || 'all');
  const [classes, setClasses] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editStudent, setEditStudent] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [stats, setStats] = useState({ total: 0, active: 0, male: 0, female: 0 });

  const selectedClass = classFilter !== 'all' ? classes.find(c => c.id === classFilter) : null;

  // Derived class stats from current student list
  const classMale = students.filter(s => s.gender === 'Male').length;
  const classFemale = students.filter(s => s.gender === 'Female').length;
  const classActive = students.filter(s => s.status === 'active').length;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ search, ...(classFilter && classFilter !== 'all' && { class_id: classFilter }) });
      const res = await api.get(`/students?${params}&limit=100`);
      const data = res.data || [];
      setStudents(data);
      setTotal(res.total || data.length);
    } catch { toast.error('Failed to load students'); }
    finally { setLoading(false); }
  }, [search, classFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/academics/classes').then(r => setClasses(r.data || [])).catch(() => {});
    loadStats();
  }, []);

  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId) {
      api.get(`/students/${editId}`).then(r => { if (r.success) setEditStudent(r.data); }).catch(() => {});
    }
  }, [searchParams]);

  const loadStats = async () => {
    try {
      const res = await api.get('/students/stats');
      if (res.success) setStats(res.data);
    } catch {}
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/students/${deleteId}`);
      toast.success('Student removed');
      setDeleteId(null);
      load(); loadStats();
    } catch { toast.error('Failed to remove student'); }
  };

  const handleExportCSV = () => {
    const headers = ['admission_number', 'first_name', 'last_name', 'gender', 'class_name', 'date_of_birth', 'parent_name', 'parent_phone', 'address', 'status'];
    const rows = students.map(s => headers.map(h => `"${(s[h] || '').toString().replace(/"/g, '""')}"`).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const a = document.createElement('a');
    a.href = `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
    a.download = selectedClass ? `${selectedClass.name.replace(/\s+/g, '_')}_students.csv` : 'all_students.csv';
    a.click();
  };

  const handlePrintClassList = () => {
    const className = selectedClass?.name || 'All Students';
    const dateStr = new Date().toLocaleDateString('en-NG', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    const printWindow = window.open('', '_blank', 'width=900,height=1100');
    const rows = students.map((s, i) => `
      <tr style="border-bottom:1px solid #f1f5f9; ${i%2===0?'background:#f8fafc':''}">
        <td style="padding:10px 14px; font-size:12px; color:#64748b; text-align:center;">${i+1}</td>
        <td style="padding:10px 14px;">
          <div style="display:flex;align-items:center;gap:10px;">
            <div style="width:34px;height:34px;border-radius:50%;background:#eff6ff;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:800;color:#2563eb;flex-shrink:0;">
              ${(s.first_name?.[0]||'')+(s.last_name?.[0]||'')}
            </div>
            <div>
              <div style="font-size:13px;font-weight:700;color:#1e293b;">${s.first_name} ${s.last_name}</div>
              ${s.date_of_birth ? `<div style="font-size:11px;color:#94a3b8;">DOB: ${s.date_of_birth}</div>` : ''}
            </div>
          </div>
        </td>
        <td style="padding:10px 14px;font-size:12px;color:#475569;font-family:monospace;">${s.admission_number||'—'}</td>
        <td style="padding:10px 14px;font-size:12px;color:#475569;">${s.class_name||'—'}${s.section?' '+s.section:''}</td>
        <td style="padding:10px 14px;">
          <span style="font-size:11px;font-weight:600;padding:2px 8px;border-radius:20px;${s.gender==='Male'?'background:#eff6ff;color:#2563eb':'background:#fdf2f8;color:#db2777'}">
            ${s.gender||'—'}
          </span>
        </td>
        <td style="padding:10px 14px;font-size:12px;color:#475569;">${s.parent_name||'—'}</td>
        <td style="padding:10px 14px;font-size:12px;color:#475569;">${s.parent_phone||'—'}</td>
      </tr>`).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
<title>Class List – ${className}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:'Segoe UI',Arial,sans-serif; background:#fff; color:#1e293b; }
  .card { max-width:860px; margin:20px auto; border:1.5px solid #e2e8f0; border-radius:14px; overflow:hidden; }
  .header { background:#2563eb; color:white; padding:22px 28px; }
  .header h1 { font-size:22px; font-weight:800; letter-spacing:-0.5px; }
  .header p { opacity:0.8; font-size:13px; margin-top:4px; }
  .meta { display:flex; gap:24px; padding:14px 28px; background:#f8fafc; border-bottom:1px solid #e2e8f0; }
  .meta-item { display:flex; align-items:center; gap:8px; }
  .meta-label { font-size:11px; text-transform:uppercase; letter-spacing:0.5px; color:#94a3b8; font-weight:600; }
  .meta-val { font-size:15px; font-weight:800; color:#1e293b; }
  table { width:100%; border-collapse:collapse; }
  thead th { text-align:left; padding:10px 14px; font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:0.7px; color:#94a3b8; background:#f8fafc; border-bottom:1px solid #e2e8f0; }
  .footer { padding:12px 28px; text-align:center; font-size:10px; color:#94a3b8; background:#f8fafc; border-top:1px solid #e2e8f0; }
  @media print { body { -webkit-print-color-adjust:exact; print-color-adjust:exact; } .card { border:none; margin:0; } }
</style></head><body>
  <div class="card">
    <div class="header">
      <h1>${className} — Student Register</h1>
      <p>Printed on ${dateStr}</p>
    </div>
    <div class="meta">
      <div class="meta-item"><div><div class="meta-label">Total</div><div class="meta-val">${students.length}</div></div></div>
      <div class="meta-item"><div><div class="meta-label">Male</div><div class="meta-val">${classMale}</div></div></div>
      <div class="meta-item"><div><div class="meta-label">Female</div><div class="meta-val">${classFemale}</div></div></div>
      <div class="meta-item"><div><div class="meta-label">Active</div><div class="meta-val">${classActive}</div></div></div>
    </div>
    <table>
      <thead><tr>
        <th style="width:42px;">#</th>
        <th>Student</th><th>Adm. No.</th><th>Class</th><th>Gender</th><th>Guardian</th><th>Phone</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="footer">EDUVA School Management System · ${students.length} student${students.length!==1?'s':''} in ${className}</div>
  </div>
  <script>window.onload=function(){window.print();window.onafterprint=function(){window.close();};};</script>
</body></html>`;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto space-y-5" data-testid="students-page">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Students</h1>
          <p className="text-sm text-slate-500 mt-0.5">{stats.total} total enrolled</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowBulkImport(true)}
            className="flex items-center gap-1.5 border border-blue-200 text-blue-600 bg-blue-50 text-sm font-semibold px-3 py-2 rounded-xl hover:bg-blue-100 transition-colors"
            data-testid="bulk-import-btn">
            <Upload className="w-4 h-4" /> Bulk Import
          </button>
          <button onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1.5 bg-[#2563eb] hover:bg-blue-700 text-white text-sm font-bold px-4 py-2 rounded-xl transition-colors"
            data-testid="add-student-btn">
            <Plus className="w-4 h-4" /> Add Student
          </button>
        </div>
      </div>

      {/* ── Overall stats (shown when no class selected) ── */}
      {!selectedClass && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total Students', value: stats.total, icon: Users, bg: 'bg-blue-100', txt: 'text-blue-600' },
            { label: 'Active', value: stats.active, icon: UserCheck, bg: 'bg-emerald-100', txt: 'text-emerald-600' },
            { label: 'Male', value: stats.male, icon: GraduationCap, bg: 'bg-sky-100', txt: 'text-sky-600' },
            { label: 'Female', value: stats.female, icon: GraduationCap, bg: 'bg-pink-100', txt: 'text-pink-600' },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g,'-')}`}>
              <div className={`w-8 h-8 rounded-xl flex items-center justify-center mb-2 ${s.bg}`}>
                <s.icon className={`w-4 h-4 ${s.txt}`} />
              </div>
              <p className="text-2xl font-extrabold text-slate-900">{s.value}</p>
              <p className="text-xs text-slate-500 font-medium mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* ── Class Dashboard Banner (shown when class is selected) ── */}
      {selectedClass && (
        <div className="bg-[#2563eb] rounded-2xl overflow-hidden shadow-md" data-testid="class-dashboard-banner">
          {/* Dot pattern */}
          <div className="relative">
            <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(circle,#fff 1px,transparent 1px)', backgroundSize: '20px 20px' }} />
            <div className="relative px-6 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <BookOpen className="w-5 h-5 text-blue-200" />
                  <p className="text-blue-200 text-xs font-semibold uppercase tracking-widest">Class Dashboard</p>
                </div>
                <h2 className="text-2xl font-extrabold text-white tracking-tight">{selectedClass.name}</h2>
                <p className="text-blue-100 text-sm mt-0.5">{students.length} student{students.length !== 1 ? 's' : ''} enrolled</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={handlePrintClassList} disabled={loading || students.length === 0}
                  className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 disabled:opacity-40 text-white text-sm font-semibold px-3 py-2 rounded-xl border border-white/20 transition-all"
                  data-testid="print-class-btn">
                  <Printer className="w-3.5 h-3.5" /> Print List
                </button>
                <button onClick={handleExportCSV} disabled={loading || students.length === 0}
                  className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 disabled:opacity-40 text-white text-sm font-semibold px-3 py-2 rounded-xl border border-white/20 transition-all"
                  data-testid="export-class-btn">
                  <Download className="w-3.5 h-3.5" /> Export CSV
                </button>
                <button onClick={() => setShowAddModal(true)}
                  className="flex items-center gap-1.5 bg-white text-blue-700 hover:bg-blue-50 text-sm font-bold px-3 py-2 rounded-xl transition-all shadow-sm"
                  data-testid="add-student-class-btn">
                  <Plus className="w-3.5 h-3.5" /> Add Student
                </button>
              </div>
            </div>

            {/* Class stats strip */}
            <div className="relative grid grid-cols-4 border-t border-white/10">
              {[
                { label: 'Total', value: students.length, color: 'text-white' },
                { label: 'Active', value: classActive, color: 'text-emerald-200' },
                { label: 'Male', value: classMale, color: 'text-sky-200' },
                { label: 'Female', value: classFemale, color: 'text-pink-200' },
              ].map((stat, i) => (
                <div key={stat.label} className={`py-3 text-center ${i > 0 ? 'border-l border-white/10' : ''}`}>
                  <p className={`text-xl font-extrabold ${stat.color}`}>{stat.value}</p>
                  <p className="text-blue-200 text-xs font-semibold mt-0.5 uppercase tracking-wide">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Search + Filter bar ── */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-3 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={selectedClass ? `Search in ${selectedClass.name}...` : 'Search by name or admission number...'}
            className="w-full h-10 pl-9 pr-9 text-sm border border-slate-200 rounded-xl outline-none focus:border-blue-400 transition-colors"
            data-testid="student-search"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-600">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <select value={classFilter} onChange={e => setClassFilter(e.target.value)}
              className="h-10 pl-3 pr-8 text-sm border border-slate-200 rounded-xl bg-white outline-none focus:border-blue-400 appearance-none min-w-[160px]"
              data-testid="class-filter">
              <option value="all">All Classes</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
          {/* Export button visible when no class selected */}
          {!selectedClass && (
            <button onClick={handleExportCSV}
              className="flex items-center gap-1.5 border border-slate-200 text-slate-600 text-sm font-semibold px-3 py-2 rounded-xl hover:bg-slate-50 transition-colors h-10"
              data-testid="export-all-btn">
              <Download className="w-4 h-4" /> Export
            </button>
          )}
        </div>
      </div>

      {/* ── Students table ── */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="students-list">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-7 h-7 text-blue-600 animate-spin" />
          </div>
        ) : students.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-slate-400" />
            </div>
            <p className="font-bold text-slate-700">No students found</p>
            <p className="text-sm text-slate-400 mt-1">
              {search ? 'Try a different search term' : selectedClass ? `No students in ${selectedClass.name} yet` : 'Add your first student to get started'}
            </p>
            {!search && (
              <button onClick={() => setShowAddModal(true)}
                className="mt-4 inline-flex items-center gap-1.5 bg-[#2563eb] text-white text-sm font-semibold px-4 py-2 rounded-xl hover:bg-blue-700 transition-colors">
                <Plus className="w-4 h-4" /> Add Student
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/60">
                  <th className="text-left px-5 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide">Student</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide hidden md:table-cell">Adm. No.</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide hidden sm:table-cell">Class</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide hidden lg:table-cell">Gender</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-bold text-slate-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody>
                {students.map((s, i) => (
                  <tr key={s.id}
                    onClick={() => navigate(`/school/students/${s.id}`)}
                    className="border-b border-slate-50 hover:bg-blue-50/30 cursor-pointer transition-colors group"
                    data-testid={`student-row-${s.id}`}>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        {s.avatar ? (
                          <img src={s.avatar} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0 ring-2 ring-blue-100" />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 text-blue-600 text-xs font-extrabold">
                            {s.first_name?.[0]}{s.last_name?.[0]}
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-bold text-slate-800 group-hover:text-blue-700 transition-colors">{s.first_name} {s.last_name}</p>
                          <p className="text-xs text-slate-400 md:hidden">{s.admission_number}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-slate-500 hidden md:table-cell font-mono text-xs">{s.admission_number}</td>
                    <td className="px-4 py-3.5 hidden sm:table-cell">
                      <span className="text-xs font-semibold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-lg">{s.class_name || '—'}{s.section ? ' ' + s.section : ''}</span>
                    </td>
                    <td className="px-4 py-3.5 hidden lg:table-cell">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${s.gender === 'Male' ? 'bg-blue-50 text-blue-600' : 'bg-pink-50 text-pink-600'}`}>
                        {s.gender}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${s.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {s.status}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-right" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={e => { e.stopPropagation(); setEditStudent(s); }}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                          title="Edit student" data-testid={`edit-student-${s.id}`}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={e => { e.stopPropagation(); setDeleteId(s.id); }}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          title="Remove student" data-testid={`delete-student-${s.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-400 ml-1 transition-colors" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-5 py-3 border-t border-slate-50 flex items-center justify-between">
              <p className="text-xs text-slate-400">Showing {students.length}{selectedClass ? ` of ${total} in ${selectedClass.name}` : ` of ${total} students`}</p>
              {selectedClass && students.length > 0 && (
                <div className="flex items-center gap-2">
                  <button onClick={handlePrintClassList}
                    className="flex items-center gap-1 text-xs text-slate-500 hover:text-blue-600 hover:bg-blue-50 px-2 py-1 rounded-lg transition-colors">
                    <Printer className="w-3 h-3" /> Print
                  </button>
                  <button onClick={handleExportCSV}
                    className="flex items-center gap-1 text-xs text-slate-500 hover:text-blue-600 hover:bg-blue-50 px-2 py-1 rounded-lg transition-colors">
                    <Download className="w-3 h-3" /> Export
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showAddModal && (
        <StudentModal student={null} classes={classes} defaultClassId={classFilter !== 'all' ? classFilter : ''} onClose={() => setShowAddModal(false)} onSuccess={() => { setShowAddModal(false); load(); loadStats(); }} />
      )}
      {editStudent && (
        <StudentModal student={editStudent} classes={classes}
          onClose={() => { setEditStudent(null); setSearchParams({}); }}
          onSuccess={() => { setEditStudent(null); setSearchParams({}); load(); loadStats(); }} />
      )}
      {showBulkImport && (
        <BulkImportModal classes={classes} onClose={() => setShowBulkImport(false)} onSuccess={() => { setShowBulkImport(false); load(); loadStats(); }} />
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Student?</AlertDialogTitle>
            <AlertDialogDescription>This student will be marked as removed. Their academic records will be preserved.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ─── Student Add/Edit Modal ────────────────────────────────────── */
function StudentModal({ student, classes, defaultClassId, onClose, onSuccess }) {
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', gender: 'Male', class_id: defaultClassId || '', class_name: '',
    section: 'A', date_of_birth: '', parent_name: '', parent_phone: '',
    address: '', phone: '', blood_group: '', medical_notes: '', avatar: null,
    ...(student || {}),
  });
  const photoRef = useRef(null);

  const upd = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleClassChange = (classId) => {
    const cls = classes.find(c => c.id === classId);
    upd('class_id', classId);
    if (cls) upd('class_name', cls.name);
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Photo must be under 5MB'); return; }
    setUploadingPhoto(true);
    try {
      const url = await uploadToCloudinary(file, 'eduva/students');
      upd('avatar', url);
      toast.success('Photo uploaded');
    } catch {
      toast.error('Photo upload failed');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) { toast.error('First and last name are required'); return; }
    setSaving(true);
    try {
      const res = student?.id
        ? await api.put(`/students/${student.id}`, form)
        : await api.post('/students', form);
      if (!student?.id && res.username) {
        toast.success(`Student added! Login: username="${res.username}", password=their phone number`, { duration: 6000 });
      } else {
        toast.success(student ? 'Student updated' : 'Student added');
      }
      onSuccess();
    } catch (err) { toast.error(err?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-outfit text-lg">{student ? 'Edit Student' : 'Add New Student'}</DialogTitle>
        </DialogHeader>

        {/* Photo Upload */}
        <div className="flex items-center gap-4 pb-2">
          <div className="relative">
            {form.avatar ? (
              <img src={form.avatar} alt="Preview" className="w-20 h-20 rounded-2xl object-cover border-2 border-indigo-200" />
            ) : (
              <div className="w-20 h-20 rounded-2xl bg-indigo-50 border-2 border-dashed border-indigo-200 flex flex-col items-center justify-center">
                <Camera className="w-6 h-6 text-indigo-400" />
                <span className="text-[10px] text-indigo-400 mt-1">Photo</span>
              </div>
            )}
            <button onClick={() => !uploadingPhoto && photoRef.current?.click()}
              className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center shadow-md hover:bg-indigo-700 disabled:opacity-60"
              disabled={uploadingPhoto}
              data-testid="upload-photo-btn">
              {uploadingPhoto ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
            </button>
          </div>
          <div className="text-sm text-slate-500">
            <p className="font-semibold text-slate-700">Student Photo</p>
            <p className="text-xs">{uploadingPhoto ? 'Uploading...' : 'Click camera to upload (max 5MB)'}</p>
            {form.avatar && !uploadingPhoto && (
              <button onClick={() => upd('avatar', null)} className="text-xs text-red-500 hover:underline mt-0.5">Remove photo</button>
            )}
          </div>
          <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} data-testid="photo-input" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          {[
            { k: 'first_name', l: 'First Name *', ph: 'Adaeze' },
            { k: 'last_name', l: 'Last Name *', ph: 'Okonkwo' },
          ].map(f => (
            <div key={f.k}>
              <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">{f.l}</Label>
              <Input value={form[f.k]} onChange={e => upd(f.k, e.target.value)} placeholder={f.ph} className="h-9 text-sm" />
            </div>
          ))}

          <div>
            <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Gender</Label>
            <Select value={form.gender} onValueChange={v => upd('gender', v)}>
              <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{GENDERS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Date of Birth</Label>
            <Input type="date" value={form.date_of_birth} onChange={e => upd('date_of_birth', e.target.value)} className="h-9 text-sm" />
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Class</Label>
            <Select value={form.class_id} onValueChange={handleClassChange}>
              <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Select class" /></SelectTrigger>
              <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Section</Label>
            <Select value={form.section} onValueChange={v => upd('section', v)}>
              <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{SECTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Blood Group</Label>
            <Select value={form.blood_group || ''} onValueChange={v => upd('blood_group', v)}>
              <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Parent / Guardian Name</Label>
            <Input value={form.parent_name} onChange={e => upd('parent_name', e.target.value)} placeholder="Mr. Okonkwo" className="h-9 text-sm" />
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Parent Phone</Label>
            <Input value={form.parent_phone} onChange={e => upd('parent_phone', e.target.value)} placeholder="+234 801..." className="h-9 text-sm" />
          </div>

          <div className="col-span-2">
            <Label className="text-xs font-semibold text-slate-600 mb-1.5 block flex items-center gap-1.5">
              Student Phone
              <span className="text-[10px] font-normal text-indigo-500 bg-indigo-50 px-1.5 py-0.5 rounded-full">Used as login password</span>
            </Label>
            <Input value={form.phone} onChange={e => upd('phone', e.target.value)} placeholder="+234 810..." className="h-9 text-sm" data-testid="student-phone-input" />
            {!student && form.phone && form.first_name && (
              <p className="text-[11px] text-indigo-600 mt-1">Login will be created: username=<strong>{form.first_name.toLowerCase().trim()}</strong>, password=phone number</p>
            )}
          </div>

          <div className="col-span-2">
            <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Home Address</Label>
            <Input value={form.address} onChange={e => upd('address', e.target.value)} placeholder="Home address" className="h-9 text-sm" />
          </div>

          <div className="col-span-2">
            <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Medical Notes (optional)</Label>
            <Input value={form.medical_notes} onChange={e => upd('medical_notes', e.target.value)} placeholder="Allergies, conditions, etc." className="h-9 text-sm" />
          </div>
        </div>

        <div className="flex gap-3 pt-1">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white">
            {saving ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Saving...</> : student ? 'Update Student' : 'Add Student'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Bulk Import Modal ─────────────────────────────────────────── */
function BulkImportModal({ classes, onClose, onSuccess }) {
  const [step, setStep] = useState('upload');   // upload | preview | result
  const [rows, setRows] = useState([]);
  const [result, setResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [defaultClass, setDefaultClass] = useState('');
  const fileRef = useRef(null);

  const TEMPLATE_HEADERS = ['first_name', 'last_name', 'gender', 'class_name', 'date_of_birth', 'admission_number', 'parent_name', 'parent_phone', 'address', 'section', 'phone'];

  const downloadTemplate = () => {
    const sample = 'first_name,last_name,gender,class_name,date_of_birth,admission_number,parent_name,parent_phone,address,section,phone\nAdaeze,Okonkwo,Female,JSS 1,2012-03-15,,Mr. Okonkwo,08012345678,12 Main St,A,08098765432';
    const a = document.createElement('a');
    a.href = `data:text/csv;charset=utf-8,${encodeURIComponent(sample)}`;
    a.download = 'students_import_template.csv';
    a.click();
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const lines = ev.target.result.split('\n').filter(l => l.trim());
      if (lines.length < 2) { toast.error('CSV appears empty'); return; }
      const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
      const parsed = lines.slice(1).map(line => {
        const vals = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
        return Object.fromEntries(headers.map((h, i) => [h, vals[i] || '']));
      }).filter(r => r.first_name || r.last_name);
      setRows(parsed);
      setStep('preview');
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const cls = classes.find(c => c.id === defaultClass);
      const enriched = rows.map(r => ({
        ...r,
        class_id: r.class_id || defaultClass || '',
        class_name: r.class_name || cls?.name || '',
      }));
      const res = await api.post('/students/bulk-import', { students: enriched });
      setResult(res.data);
      setStep('result');
    } catch (err) { toast.error(err?.detail || 'Import failed'); }
    finally { setImporting(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-outfit text-lg flex items-center gap-2">
            <Upload className="w-5 h-5 text-indigo-600" /> Bulk Import Students
          </DialogTitle>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-4">
            {/* Info */}
            <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 text-sm text-indigo-700">
              <p className="font-semibold mb-1">Import Instructions</p>
              <ul className="text-xs space-y-0.5 text-indigo-600 list-disc ml-4">
                <li>Upload a CSV file with student data</li>
                <li>Required: first_name, last_name</li>
                <li>Add a <strong>phone</strong> column to auto-create student login accounts</li>
                <li>Login: username = first name, password = phone number</li>
                <li>Maximum 200 students per import</li>
              </ul>
            </div>

            <button onClick={downloadTemplate}
              className="w-full flex items-center gap-2 border-2 border-dashed border-indigo-200 text-indigo-600 rounded-xl p-3.5 hover:bg-indigo-50 transition-colors text-sm font-semibold"
              data-testid="download-template-btn">
              <FileText className="w-4 h-4" /> Download CSV Template
            </button>

            {/* Default class (optional) */}
            <div>
              <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Default Class (if not in CSV)</Label>
              <div className="relative">
                <select value={defaultClass} onChange={e => setDefaultClass(e.target.value)}
                  className="w-full h-9 text-sm border border-slate-200 rounded-lg px-3 bg-white outline-none focus:border-indigo-400 appearance-none pr-8">
                  <option value="">No default class</option>
                  {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </div>

            {/* File drop zone */}
            <div
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-slate-300 hover:border-indigo-400 rounded-2xl p-10 text-center cursor-pointer transition-colors group">
              <Upload className="w-10 h-10 text-slate-300 group-hover:text-indigo-400 mx-auto mb-3 transition-colors" />
              <p className="font-semibold text-slate-600 group-hover:text-indigo-600 transition-colors">Click to upload CSV</p>
              <p className="text-xs text-slate-400 mt-1">or drag and drop here</p>
              <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFile} data-testid="csv-file-input" />
            </div>
          </div>
        )}

        {step === 'preview' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <span className="bg-indigo-100 text-indigo-700 font-bold px-2.5 py-0.5 rounded-full">{rows.length}</span>
                <span className="text-slate-600">students ready to import</span>
              </div>
              <button onClick={() => setStep('upload')} className="text-sm text-slate-400 hover:text-slate-600">← Back</button>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200 max-h-64 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-bold text-slate-500">#</th>
                    <th className="text-left px-3 py-2 font-bold text-slate-500">Name</th>
                    <th className="text-left px-3 py-2 font-bold text-slate-500 hidden sm:table-cell">Gender</th>
                    <th className="text-left px-3 py-2 font-bold text-slate-500 hidden sm:table-cell">Class</th>
                    <th className="text-left px-3 py-2 font-bold text-slate-500">DOB</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className={`border-t border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}>
                      <td className="px-3 py-2 text-slate-400">{i + 1}</td>
                      <td className="px-3 py-2 font-semibold text-slate-700">{r.first_name} {r.last_name}</td>
                      <td className="px-3 py-2 text-slate-500 hidden sm:table-cell">{r.gender || '—'}</td>
                      <td className="px-3 py-2 text-slate-500 hidden sm:table-cell">{r.class_name || '—'}</td>
                      <td className="px-3 py-2 text-slate-400">{r.date_of_birth || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep('upload')} className="flex-1">Cancel</Button>
              <Button onClick={handleImport} disabled={importing} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="confirm-import-btn">
                {importing ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Importing...</> : `Import ${rows.length} Students`}
              </Button>
            </div>
          </div>
        )}

        {step === 'result' && result && (
          <div className="space-y-4 text-center">
            <div className={`w-16 h-16 mx-auto rounded-2xl flex items-center justify-center ${result.imported > 0 ? 'bg-green-100' : 'bg-red-100'}`}>
              {result.imported > 0
                ? <UserCheck className="w-8 h-8 text-green-600" />
                : <UserX className="w-8 h-8 text-red-600" />
              }
            </div>
            <div>
              <p className="text-xl font-extrabold text-slate-900">{result.imported} Imported</p>
              {result.accounts_created > 0 && (
                <p className="text-sm text-indigo-600 font-semibold mt-1">{result.accounts_created} login accounts created</p>
              )}
              {result.failed > 0 && <p className="text-sm text-red-600 font-semibold">{result.failed} failed</p>}
            </div>
            {result.errors?.length > 0 && (
              <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-left text-xs text-red-700 max-h-32 overflow-y-auto space-y-1">
                {result.errors.map((e, i) => <p key={i}>{e}</p>)}
              </div>
            )}
            <Button onClick={() => { onSuccess(); }} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="import-done-btn">
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
